import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Loader2 , Wifi , AlertTriangle , Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePhoneOperator } from '@/hooks/usePhoneOperator';
import { normalizarOperadora } from '@/utils/operadoraNormalizer';
import { rotuloTipoChip } from '@/lib/tipoChipAtivacao';

interface LinhaCompletaFieldProps {
  ativacaoChip?: boolean;
  ddd: string;
  linha: string;
  gb?: string | null;
  operadoraDoadora?: string | null;
  tipoChip?: string | null;
  /** TT e renovação mantêm o chip do cliente: não há tipo a escolher. */
  semChip?: boolean;
  isHA?: boolean;
  isFIBRA?: boolean;
  semLinhaFixa?: boolean;
  disabled?: boolean; //  Bloqueia edição pós-fechamento
  // 🆕 Campos de aparelho
  aparelhoValor?: string | null;
  aparelhoModelo?: string | null;
  aparelhoParcelas?: string | null;
  onDDDChange: (value: string) => void;
  onLinhaChange: (value: string) => void;
  onGBChange: (value: string) => void;
  onOperadoraDetectada: (operadora: string) => void;
  onTipoChipChange: (value: string) => void;
  onSemLinhaFixaChange?: (value: boolean) => void; //  Callback para mudança de "sem linha fixa"
  // 🆕 Callbacks de aparelho
  onAparelhoValorChange?: (value: string) => void;
  onAparelhoModeloChange?: (value: string) => void;
  onAparelhoParcelasChange?: (value: string) => void;
  obrigatorios?: {
    ddd?: boolean;
    linha?: boolean;
    operadoraDoadora?: boolean;
    tipoChip?: boolean;
  };
  erros?: {
    ddd?: string;
    linha?: string;
    gb?: string;
    operadoraDoadora?: string;
    tipoChip?: string;
  };
}

/**
 * Campo compacto: DDD + LINHA + GB + OPERADORA DOADORA + TIPO DE CHIP
 * Tudo em uma única linha (desktop) ou 2 linhas (mobile)
 * Operadora é detectada automaticamente via API
 */
export const LinhaCompletaField = React.memo(function LinhaCompletaField({
  ddd,
  linha,
  gb,
  operadoraDoadora,
  tipoChip,
  semChip = false,
  isHA = false,
  ativacaoChip = false,
  isFIBRA = false,
  semLinhaFixa = false,
  disabled = false,
  // 🆕 Props de aparelho
  aparelhoValor,
  aparelhoModelo,
  aparelhoParcelas,
  onDDDChange,
  onLinhaChange,
  onGBChange,
  onOperadoraDetectada,
  onTipoChipChange,
  onSemLinhaFixaChange,
  // 🆕 Callbacks de aparelho
  onAparelhoValorChange,
  onAparelhoModeloChange,
  onAparelhoParcelasChange,
  obrigatorios = {},
  erros = {},
}: LinhaCompletaFieldProps) {
  // Estado local para input responsivo
  const [dddLocal, setDddLocal] = useState(ddd);
  const [linhaLocal, setLinhaLocal] = useState(linha);
  const [gbLocal, setGbLocal] = useState(gb || '');
  const [tipoChipLocal, setTipoChipLocal] = useState(tipoChip);
  
  // 🆕 Estados locais para aparelho
  const [aparelhoValorLocal, setAparelhoValorLocal] = useState(aparelhoValor || '');
  const [aparelhoModeloLocal, setAparelhoModeloLocal] = useState(aparelhoModelo || '');
  const [aparelhoParcelasLocal, setAparelhoParcelasLocal] = useState(aparelhoParcelas || '');
  
  // Controle de edição ativa para evitar conflito com sincronização
  const [isUserEditing, setIsUserEditing] = useState(false);
  const editTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sincronizar props com estado local
  useEffect(() => {
    setDddLocal(ddd);
  }, [ddd]);

  useEffect(() => {
    // ✅ NÃO sincronizar se usuário está digitando
    if (!isUserEditing && linha !== linhaLocal) {
      console.log(`🔄 [SYNC LINHA] ${linhaLocal} → ${linha}`);
      setLinhaLocal(linha);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linha, isUserEditing]);

  useEffect(() => {
    // ✅ Só sincronizar se valor vier de fora E for diferente do local
    if (tipoChip !== tipoChipLocal && tipoChip !== undefined) {
      console.log(`🔄 [SYNC TIPO_CHIP] ${tipoChipLocal} → ${tipoChip}`);
      setTipoChipLocal(tipoChip);
    }
  }, [tipoChip]);

  useEffect(() => {
    if (gb !== gbLocal && gb !== undefined) {
      setGbLocal(gb || '');
    }
  }, [gb]);

  // ✅ Garantir que "Número Novo" seja propagado para o pai quando isHA é true
  useEffect(() => {
    if (isHA && linha !== 'Número Novo') {
      console.log('🆕 [HA] Propagando "Número Novo" para o pai');
      onLinhaChange('Número Novo');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHA, linha]);

  // ✅ FIBRA: Auto-setar tipo_chip como "fixo" quando for linha fixa (E tiver linha fixa)
  useEffect(() => {
    if (isFIBRA && !semLinhaFixa && tipoChip !== 'fixo') {
      console.log('📞 [FIBRA] Auto-setando tipo_chip como "fixo"');
      onTipoChipChange('fixo');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFIBRA, semLinhaFixa, tipoChip]);

  // Construir telefone no formato (DDD) LINHA para consulta
  const telefoneCompleto = useMemo(() => {
    // 🆕 Se for HA ou FIBRA sem linha fixa, não construir telefone (não consultar API)
    if (isHA || (isFIBRA && semLinhaFixa)) return '';
    
    if (dddLocal.length === 2 && linhaLocal.length >= 8) {
      return `(${dddLocal}) ${linhaLocal}`;
    }
    return '';
  }, [dddLocal, linhaLocal, isHA, isFIBRA, semLinhaFixa]);

  // Hook de detecção de operadora
  const { operadora, isLoading, error: erroOperadora } = usePhoneOperator(telefoneCompleto);

  // Notificar operadora detectada ao componente pai (APENAS SE MUDOU)
  useEffect(() => {
    // 🆕 Se for HA, definir operadora como CLARO
    if (isHA && operadoraDoadora !== 'CLARO') {
      onOperadoraDetectada('CLARO');
      return;
    }
    
    // ✅ CORREÇÃO: Para FIBRA, aceitar "FIXO" como operadora válida
    if (operadora && operadora !== 'INVALIDO') {
      // Para FIBRA com FIXO, propagar diretamente sem normalizar
      if (isFIBRA && operadora === 'FIXO') {
        if (operadoraDoadora !== 'FIXO') {
          console.log('[LinhaCompletaField] 📞 FIBRA: Linha fixa detectada, setando operadora como FIXO');
          onOperadoraDetectada('FIXO');
        }
        return;
      }
      
      // Lógica original para operadoras móveis
      const operadoraNormalizada = normalizarOperadora(operadora);
      // Só atualizar se for diferente da operadora já salva
      if (operadoraNormalizada && operadoraNormalizada !== operadoraDoadora) {
        console.log('[LinhaCompletaField] 📡 Operadora detectada:', operadora, '→', operadoraNormalizada);
        onOperadoraDetectada(operadoraNormalizada);
      }
    }
  }, [operadora, operadoraDoadora, isHA, isFIBRA, onOperadoraDetectada]);

  // Limpar timeout ao desmontar
  useEffect(() => {
    return () => {
      if (editTimeoutRef.current) {
        clearTimeout(editTimeoutRef.current);
      }
    };
  }, []);

  // Priorizar operadora salva ou detectada
  const operadoraExibir = operadora || operadoraDoadora;
  // ✅ FIBRA: Aceitar "FIXO" como operadora válida
  const isOperadoraValid = isFIBRA 
    ? !!operadoraExibir // Para FIBRA, qualquer operadora detectada é válida (inclusive FIXO)
    : (!!operadoraExibir && operadoraExibir !== 'INVALIDO' && operadoraExibir !== 'FIXO');

  // Validações visuais
  const isDDDValid = semLinhaFixa ? true : dddLocal.length === 2;
  // ✅ FIBRA: Linha fixa tem 8 dígitos. Móvel tem 9 dígitos. Sem linha fixa: sempre válido.
  const isLinhaValid = isHA 
    ? true // HA sempre válido (não precisa de linha)
    : semLinhaFixa
      ? true // Sem linha fixa: sempre válido
      : isFIBRA 
        ? linhaLocal.length === 8 // FIBRA: exatamente 8 dígitos
        : linhaLocal.length >= 8; // Móvel: 8-9 dígitos
  const isTipoChipValid = semLinhaFixa ? true : !!tipoChip;
  const isGBValid = isFIBRA ? true : (gbLocal && gbLocal.length > 0); //  FIBRA não precisa de GB

  // Se FIBRA sem linha fixa, renderizar UI simplificada
  if (isFIBRA && semLinhaFixa) {
    return (
      <div className="space-y-3">
        {/* Switch para "Apenas Internet" */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 dark:bg-primary/30 border border-primary/40 dark:border-primary">
          <div className="flex items-center gap-2">
            <span className="text-lg"><Wifi className="h-3 w-3 text-muted-foreground/70" /></span>
            <div>
              <p className="text-sm font-medium text-primary dark:text-primary">Apenas Internet (sem linha fixa)</p>
              <p className="text-xs text-primary dark:text-primary">Produto FIBRA sem telefone fixo associado</p>
            </div>
          </div>
          <Switch
            checked={semLinhaFixa}
            disabled={disabled}
            onCheckedChange={(checked) => {
              console.log('🔄 [FIBRA] Sem linha fixa:', checked);
              onSemLinhaFixaChange?.(checked);
            }}
          />
        </div>
        
        <p className="text-xs text-success dark:text-success flex items-center gap-1">
          <span>✓</span>
          <span>FIBRA apenas internet - DDD, Linha e Operadora não são necessários</span>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Switch para "Apenas Internet" (apenas FIBRA) */}
      {isFIBRA && (
        <div className="flex items-center justify-between p-2 rounded-md bg-muted/30 border border-border/50">
          <div className="flex items-center gap-2">
            <span className="text-sm"><Wifi className="h-3 w-3 text-muted-foreground/70" /></span>
            <span className="text-xs text-muted-foreground">Apenas Internet (sem linha fixa)</span>
          </div>
          <Switch
            checked={semLinhaFixa}
            disabled={disabled}
            onCheckedChange={(checked) => {
              console.log('🔄 [FIBRA] Sem linha fixa:', checked);
              onSemLinhaFixaChange?.(checked);
            }}
          />
        </div>
      )}
      
      {/* Grid dinâmico: FIBRA tem 4 colunas (sem GB), Móvel tem 5 colunas */}
      <div className={`grid gap-2 ${isFIBRA ? 'grid-cols-[80px_1fr_140px_160px]' : 'grid-cols-[80px_1fr_100px_140px_160px]'}`}>
        
        {/* COLUNA 1: DDD */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground font-normal">
            DDD
          </Label>
           <Input
            value={dddLocal}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
              
              // ✅ Marcar que usuário está editando
              setIsUserEditing(true);
              setDddLocal(digits);
              onDDDChange(digits);
              
              // ✅ Resetar flag após 2s sem digitar (garante debounce do pai)
              if (editTimeoutRef.current) {
                clearTimeout(editTimeoutRef.current);
              }
              editTimeoutRef.current = setTimeout(() => {
                setIsUserEditing(false);
                console.log('✅ [DDD] Edição concluída - aguardou debounce do pai');
              }, 2000);
            }}
            maxLength={2}
            disabled={disabled}
            className={cn(
              'w-full text-center font-mono',
              disabled && 'bg-muted cursor-not-allowed',
              isDDDValid && 'border-success',
              erros?.ddd && 'border-destructive'
            )}
          />
        </div>

        {/* COLUNA 2: LINHA */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground font-normal">
            Linha
          </Label>
          
          {isHA ? (
            // 🆕 HA: Mostrar "Número Novo" (readonly)
            <div
              className="flex items-center justify-center h-10 px-3 rounded-md border-2 border-success bg-success/5 dark:bg-success/30 text-sm font-medium text-success dark:text-success"
            >
              Número Novo
            </div>
          ) : (
            // Lógica original: Input editável
            <Input
              value={linhaLocal}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '');
                
                // ✅ MARCAR como editando ANTES de qualquer operação
                setIsUserEditing(true);
                
                // ✅ FIBRA: Limitar a 8 dígitos (linha fixa). Móvel: 9 dígitos.
                const maxDigits = isFIBRA ? 8 : 9;
                const maxTotal = isFIBRA ? 10 : 11; // DDD (2) + linha
                
                // Se usuário digitou DDD + número completo
                if (digits.length >= maxTotal) {
                  const novoDDD = digits.slice(0, 2);
                  const novaLinha = digits.slice(2, 2 + maxDigits);
                  
                  console.log(`📞 [NUMERO COMPLETO] Separando: ${digits} → DDD: ${novoDDD}, Linha: ${novaLinha} (${isFIBRA ? 'FIBRA' : 'Móvel'})`);
                  
                  setDddLocal(novoDDD);
                  setLinhaLocal(novaLinha);
                  
                  setTimeout(() => {
                    onDDDChange(novoDDD);
                    onLinhaChange(novaLinha);
                  }, 0);
                } else {
                  const novaLinha = digits.slice(0, maxDigits);
                  setLinhaLocal(novaLinha);
                  
                  setTimeout(() => {
                    onLinhaChange(novaLinha);
                  }, 0);
                }
                
                if (editTimeoutRef.current) {
                  clearTimeout(editTimeoutRef.current);
                }
                editTimeoutRef.current = setTimeout(() => {
                  setIsUserEditing(false);
                  console.log('✅ [LINHA] Edição concluída - aguardou debounce do pai');
                }, 3000);
              }}
              placeholder={isFIBRA ? '8 dígitos' : '9 dígitos'}
              disabled={disabled}
              className={cn(
                'w-full font-mono',
                disabled && 'bg-muted cursor-not-allowed',
                isLinhaValid && 'border-success',
                erros?.linha && 'border-destructive'
              )}
            />
          )}
        </div>

        {/* COLUNA 3: GB (PACOTE DE DADOS) - Oculto para FIBRA */}
        {!isFIBRA && (
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground font-normal">
              GB
            </Label>
            <Input
              value={gbLocal}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                setGbLocal(digits);
                onGBChange(digits);
              }}
              maxLength={4}
              disabled={disabled}
              className={cn(
                'w-full text-center font-mono',
                disabled && 'bg-muted cursor-not-allowed',
                isGBValid && 'border-success',
                erros?.gb && 'border-destructive'
              )}
            />
          </div>
        )}

        {/* COLUNA 4: OPERADORA */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground font-normal">
            {isFIBRA ? 'Operadora' : 'Operadora Doadora'}
          </Label>
          {isHA ? (
            // HA: Sempre CLARO (read-only)
            <div className="flex items-center justify-center h-10 px-3 rounded-md border border-success bg-muted/30 text-sm font-medium text-success dark:text-success">
              CLARO
            </div>
          ) : isLoading ? (
            // Loading: spinner enquanto consulta API
            <div className="flex items-center justify-center h-10 px-3 rounded-md border bg-muted/30 text-sm">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            // Read-only: exibe operadora detectada pela API
            <div className={cn(
              'flex items-center justify-center h-10 px-3 rounded-md border text-sm font-medium',
              isOperadoraValid && operadoraExibir !== 'DESCONHECIDO' && 'border-success text-success dark:text-success bg-success/30 dark:bg-success/20',
              operadoraExibir === 'DESCONHECIDO' && 'border-primary text-primary dark:text-primary bg-primary/30 dark:bg-primary/20',
              (!operadoraExibir || operadoraExibir === 'INVALIDO') && obrigatorios?.operadoraDoadora && 'border-destructive',
              (!operadoraExibir || operadoraExibir === 'INVALIDO') && !obrigatorios?.operadoraDoadora && 'bg-muted/30 text-muted-foreground'
            )}>
              {operadoraExibir === 'DESCONHECIDO' ? 'DESCONHECIDO' : operadoraExibir && operadoraExibir !== 'INVALIDO' ? operadoraExibir : '-'}
            </div>
          )}
        </div>

        {/* COLUNA 5: TIPO DE CHIP */}
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground font-normal">
            Tipo
          </Label>
          
          {semChip ? (
            // TT e renovação: a linha já é do cliente e continua com o mesmo
            // número e o mesmo chip. Não há eSIM novo, não há SIMCARD, não há
            // o que escolher — e o banco apaga o campo (Douglas 23/09/2026).
            <div className="flex h-10 items-center justify-center rounded-md border bg-muted/30 px-3 text-sm font-medium text-muted-foreground">
              Sem chip
            </div>
          ) : isFIBRA ? (
            // ✅ FIBRA: Mostrar "Fixo" como valor fixo (não editável)
            <div
              className="flex items-center justify-center h-10 px-3 rounded-md border-2 border-success bg-success/5 dark:bg-success/30 text-sm font-medium text-success dark:text-success"
            >
              Fixo
            </div>
          ) : (
            // Móvel: Dropdown normal
            <>
              <Select 
                value={tipoChipLocal || ''} 
                onValueChange={(v) => {
                  if (!v || v === '') {
                    console.warn('⚠️ [VALIDAÇÃO] Tentativa de selecionar tipo_chip vazio bloqueada');
                    return;
                  }
                  
                  console.log(`🎯 [TIPO_CHIP] Selecionado:`, v);
                  console.log(`📊 [ANTES]: ${tipoChipLocal}`);
                  setTipoChipLocal(v);
                  onTipoChipChange(v);
                  console.log(`✅ [DEPOIS]: ${v}`);
                }}
                disabled={disabled}
              >
                <SelectTrigger
                  className={cn(
                    'w-full',
                    isTipoChipValid && 'border-success',
                    erros?.tipoChip && 'border-destructive',
                    !tipoChipLocal && obrigatorios?.tipoChip && 'border-danger animate-pulse'
                  )}
                >
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {ativacaoChip ? <>
                    <SelectItem value="esim_real">ESIM - REAL</SelectItem>
                    <SelectItem value="esim_simcard">ESIM - SIMCARD</SelectItem>
                    {tipoChipLocal && !['esim_real', 'esim_simcard'].includes(tipoChipLocal) && <SelectItem value={tipoChipLocal} disabled>{rotuloTipoChip(tipoChipLocal)}</SelectItem>}
                  </> : <>
                  <SelectItem value="e-sim">E-SIM</SelectItem>
                  <SelectItem value="fisico">Chip Físico</SelectItem>
                  <SelectItem value="e_sim_fisico">Ambos (E-SIM + Físico)</SelectItem>
                  <SelectItem value="aparelho"> Aparelho</SelectItem>
                  </>}
                </SelectContent>
              </Select>
              {!isTipoChipValid && obrigatorios?.tipoChip && (
                <p className="text-xs text-danger dark:text-danger flex items-center gap-1 mt-1">
                  <span><AlertTriangle className="h-3 w-3 text-primary" /></span>
                  <span>Campo obrigatório - selecione o tipo de chip</span>
                </p>
              )}
              {isTipoChipValid && tipoChipLocal && tipoChipLocal !== 'aparelho' && (
                <p className="text-xs text-success flex items-center gap-1 mt-1">
                  <span>✓</span>
                  <span>
                    {tipoChipLocal === 'esim_real' && 'ESIM - REAL selecionado'}
                    {tipoChipLocal === 'esim_simcard' && 'ESIM - SIMCARD selecionado'}
                    {tipoChipLocal === 'e-sim' && 'E-SIM selecionado'}
                    {tipoChipLocal === 'fisico' && 'Chip Físico selecionado'}
                    {tipoChipLocal === 'e_sim_fisico' && 'Ambos selecionados'}
                  </span>
                </p>
              )}
            </>
          )}
        </div>

      </div>
      
      {/*  Campos de Aparelho - Exibidos quando tipo_chip === 'aparelho'*/}
      {tipoChipLocal === 'aparelho' && (
        <div className="grid grid-cols-3 gap-2 mt-2 p-3 rounded-lg border border-primary bg-primary/5 dark:bg-primary/20">
          <div className="space-y-1">
            <Label className="text-xs font-medium text-primary dark:text-primary">Modelo do Aparelho</Label>
            <Input 
              placeholder="Ex: iPhone 15"
              value={aparelhoModeloLocal}
              onChange={(e) => {
                setAparelhoModeloLocal(e.target.value);
                onAparelhoModeloChange?.(e.target.value);
              }}
              disabled={disabled}
              className="border-primary focus:border-primary"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-primary dark:text-primary">Valor (R$)</Label>
            <Input 
              placeholder="Ex: 5000"
              value={aparelhoValorLocal}
              onChange={(e) => {
                const valor = e.target.value.replace(/\D/g, '');
                setAparelhoValorLocal(valor);
                onAparelhoValorChange?.(valor);
              }}
              disabled={disabled}
              className="border-primary focus:border-primary"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-medium text-primary dark:text-primary">Parcelas</Label>
            <Input 
              placeholder="Ex: 12"
              value={aparelhoParcelasLocal}
              onChange={(e) => {
                const parcelas = e.target.value.replace(/\D/g, '');
                setAparelhoParcelasLocal(parcelas);
                onAparelhoParcelasChange?.(parcelas);
              }}
              disabled={disabled}
              className="border-primary focus:border-primary"
              maxLength={2}
            />
          </div>
          <div className="col-span-3">
            <p className="text-xs text-primary dark:text-primary flex items-center gap-1">
              <span>Aparelho selecionado - preencha os dados acima (serão adicionados às observações)</span>
            </p>
          </div>
        </div>
      )}

      {/* Mensagens de feedback */}
      {isHA && (
        // 🆕 Feedback para HA
        <p className="text-xs text-success dark:text-success flex items-center gap-1">
          <span>✓</span>
          <span>Habilitação de linha nova (CLARO) - informe apenas DDD{semChip ? '' : ' e Tipo de Chip'}</span>
        </p>
      )}

      {isFIBRA && (
        // 📞 Feedback para FIBRA
        <p className="text-xs text-primary dark:text-primary flex items-center gap-1">
          <span><Phone className="h-3 w-3 text-muted-foreground/70" /></span>
          <span>Linha fixa (8 dígitos) - Tipo: Fixo</span>
        </p>
      )}

      {!isHA && !isFIBRA && isOperadoraValid && !isLoading && (
        // Feedback original para móvel
        <p className="text-xs text-success flex items-center gap-1">
          <span>✓</span>
          <span>Operadora detectada automaticamente</span>
        </p>
      )}

      {/* (27/07) A mensagem antiga afirmava "número sem portabilidade registrada",
          que é conclusão nossa e não o que a API disse — ela apenas não devolveu o
          nome da operadora. Num número que TEM operadora, isso mandava o consultor
          desconfiar do número em vez de da consulta. */}
      {!isHA && !isFIBRA && operadoraExibir === 'DESCONHECIDO' && !isLoading && (
        <p className="text-xs text-primary dark:text-primary">
           A consulta não devolveu a operadora deste número (será reconsultado)
        </p>
      )}

      {/* (27/07) A FALHA DA CONSULTA NÃO É CULPA DO NÚMERO. A tela dizia "Verifique
          o número" para QUALQUER motivo — inclusive API fora do ar — e mandava o
          consultor conferir um número que estava certo. Quando há erro de consulta,
          mostra o erro; "verifique o número" fica só para quando não houve erro. */}
      {!isHA && !isFIBRA && !operadoraExibir && !isLoading && isDDDValid && isLinhaValid && (
        <p className="text-xs text-destructive">
          {erroOperadora
            ? `${erroOperadora}`
            : 'Não foi possível detectar a operadora. Verifique o número.'}
        </p>
      )}

      {!isHA && operadoraExibir === 'FIXO' && (
        <p className="text-xs text-muted-foreground">
          ℹ Linha identificada como telefone fixo
        </p>
      )}
    </div>
  );
});
